<template>
  <vue-recaptcha
    ref="recaptcha"
    size="invisible"
    :sitekey="googleRecaptchaKey"
    data-test-id="google-recaptcha"
    @verify="recaptchaSuccess"
    @expired="resetExpiredCaptcha"
    @error="resetErroredCaptcha"
    @render="onRecaptchaRender"
  />
</template>

<script>
import { googleRecaptchaKey } from 'src/config'
import { VueRecaptcha } from 'vue-recaptcha'

export default {
  name: 'Recaptcha',
  components: {
    VueRecaptcha
  },
  emits: ['reset', 'success'],
  data () {
    return {
      isReady: false
    }
  },
  created () {
    _.extend(this, { googleRecaptchaKey })
  },
  mounted () {
    window.addEventListener('focus', this.handleRecaptchaDismissed)
    this.logToRollbar('Recaptcha mounted')
  },
  beforeUnmount () {
    this.logToRollbar('Recaptcha beforeUnmount')
    window.removeEventListener('focus', this.handleRecaptchaDismissed)
  },
  methods: {
    logToRollbar (message, options = {}) {
      if (!this.$isFeatureEnabled(this.$featureFlags.extra_recaptcha_info_logs)) {
        return
      }

      const level = options.level || 'info'
      options = _.omit(options, 'level')

      if (level === 'info') {
        this.$rollbar.info(message, options)
      } else if (level === 'error') {
        this.$rollbar.error(message, options)
      }
    },
    onRecaptchaRender () {
      this.isReady = true
      this.logToRollbar('Recaptcha rendered and ready')
    },
    isRecaptchaReady () {
      return this.isReady && this.$refs.recaptcha && typeof this.$refs.recaptcha.execute === 'function'
    },
    resetCaptcha (notify = true) {
      this.executing = false
      this.logToRollbar('Reset recaptcha', { notify })
      this.$refs.recaptcha.reset()
      if (notify) {
        this.$emit('reset')
      }
    },
    resetExpiredCaptcha () {
      this.logToRollbar('Recaptcha expired')
      this.resetCaptcha()
    },
    resetErroredCaptcha () {
      this.logToRollbar('Recaptcha error', { level: 'error' })
      this.resetCaptcha()
    },
    executeCaptcha () {
      this.logToRollbar('Execute recaptcha')
      if (this.isRecaptchaReady()) {
        this.$refs.recaptcha.execute()
        this.executing = true
      } else {
        throw new Error('ReCAPTCHA not ready')
      }
    },
    recaptchaSuccess (token) {
      this.executing = false
      this.logToRollbar('Recaptcha success')
      this.$emit('success', token)
      this.$refs.recaptcha.reset()
    },
    isRecaptchaChallengeActive () {
      // When the recaptcha challenge is active, the active element is a iframe with the src containing 'bframe'
      const recaptchaChallengeSrcIdentifier = 'bframe'
      return !_.isNil(document.activeElement?.src) && _.includes(document.activeElement.src, recaptchaChallengeSrcIdentifier)
    },
    handleRecaptchaDismissed () {
      // Here we only check if we are still executing recaptcha
      // If so we set a timeout to check if the recaptcha is still active
      // The active element is still recaptcha challenge at this point, so we need to set a timeout to check if the recaptcha is still active
      if (this.executing) {
        setTimeout(() => {
          // Set a timeout to allow for a successful execution of the recaptcha
          // If the recaptcha is still executing, it means the challenge was dismissed
          if (this.executing && !this.isRecaptchaChallengeActive()) {
            this.logToRollbar('Window regained focus - possibly challenge was dismissed')
            this.resetCaptcha()
          }
        }, 2000)
      }
    }
  }
}

</script>
